import { type PaginatedDocs } from 'payload'
import type { Config } from '@/payload-types'
import { createLogger } from '@/lib/logger'

enum PayloadStatus {
  OK = 200,
  NOT_FOUND = 404,
  SERVER_ERROR = 500,
}

type PayloadCollectionSlug = keyof Config['collections']
export type CollectionEntity<T extends PayloadCollectionSlug> = Config['collections'][T] & {
  media: Config['collections']['media'] // media collection is often aliased to icon/screenshot, etc
}

type PopulateKeys<T extends PayloadCollectionSlug> = keyof Config['collections'][T]

export type PopulateFields = Omit<
  | {
      [K in PayloadCollectionSlug]?: {
        [CK in PopulateKeys<K>]?: true
      }
    }
  | {
      [K in PayloadCollectionSlug]?: {
        [CK in PopulateKeys<K>]?: false
      }
    },
  'payload-kv' | 'payload-locked-documents' | 'payload-preferences' | 'payload-migrations'
>

// For hasMany select fields, T is an array (e.g. ('app' | 'comparison')[] | null).
// Payload supports `equals` on individual elements at runtime, so we widen accordingly.
// NonNullable strips null/undefined before the array check; tuple wrapping prevents union distribution.
type OrElement<T> = [NonNullable<T>] extends [(infer U)[]] ? T | U : T
type InArray<T> = [NonNullable<T>] extends [(infer U)[]] ? U[] : T[]

type WhereOperators<T = any> = {
  equals?: OrElement<T>
  not_equals?: OrElement<T>
  greater_than?: T
  greater_than_equal?: T
  less_than?: T
  less_than_equal?: T
  like?: string
  contains?: OrElement<T>
  in?: InArray<T>
  not_in?: InArray<T>
  all?: InArray<T>
  exists?: boolean
}

type WhereFieldConstraints<T> = {
  [K in keyof T]?: T[K] extends object
    ? WhereFieldConstraints<T[K]> | WhereOperators<T[K]>
    : WhereOperators<T[K]> | T[K]
}

// Dot-path keys for querying nested relationships (e.g. 'app.review._status')
type DotPathFields = {
  [key: `${string}.${string}`]: WhereOperators
  [key: `${string}.${string}.${string}`]: WhereOperators
}

// Recursive type to allow nested and/or clauses, including dot-path keys at any level
type WhereCondition<T> = WhereFieldConstraints<T> &
  DotPathFields & {
    and?: WhereCondition<T>[]
    or?: WhereCondition<T>[]
  }

type SelectFields<T> = {
  [K in keyof T]?: boolean
}

type PayloadWhereClause<T> = WhereCondition<T>

type PayloadQueryParams<T extends PayloadCollectionSlug> = {
  collection: T
  where?: PayloadWhereClause<CollectionEntity<T>>
  limit?: number
  offset?: number
  sort?: string | string[]
  depth?: number
  populate?: PopulateFields
  select?: SelectFields<CollectionEntity<T>>
  pagination?: boolean
  /**
   * Redis cache key. When provided, the query checks Redis first and only
   * hits PostgreSQL on a cache miss. Results are cached with the given TTL.
   *
   * Format: `query:{collection-alias}:{discriminator}`
   *
   * Must match `/^query:[a-zA-Z0-9_-]+:[a-zA-Z0-9_:,.-]+$/` (max 200 chars).
   * Invalid keys silently fall back to an uncached query.
   *
   * @example `query:deal:app:${appId}`
   * @example `query:acr:overlap:${[...appIds].sort().join(',')}`
   */
  cacheKey?: string
  /** Redis cache TTL in seconds. Defaults to 86 400 (24 hours). */
  cacheTtl?: number
}

type PayloadResponse<T extends PayloadCollectionSlug> = {
  status: number
  message: string
  data: PaginatedDocs<Config['collections'][T]>
}

const logger = createLogger('PayloadClient')

class HttpError extends Error {
  status: number

  constructor(status: number) {
    super(`API request failed with status ${status}`)
    this.status = status
  }
}

const isServer = typeof window === 'undefined'

const bypass = process.env.NEXT_PUBLIC_VERCEL_AUTOMATION_BYPASS_SECRET as string

const host = isServer
  ? process.env.VERCEL_URL
    ? `https://${process.env.VERCEL_URL}`
    : 'http://localhost:3000'
  : ''

async function generateClientSideHeaders() {
  return {
    'Content-Type': 'application/json',
    ...(bypass && { 'x-vercel-protection-bypass': bypass }),
  }
}

export async function queryPayload<T extends PayloadCollectionSlug>(
  params: PayloadQueryParams<T>,
): Promise<PaginatedDocs<Config['collections'][T]>> {
  const method = 'POST'
  const url = `${host}/api/payload`

  try {
    const response = await fetch(url, {
      method,
      headers: await generateClientSideHeaders(),
      credentials: 'omit',
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      throw new HttpError(response.status)
    }

    const responseJson = (await response.json()) as PayloadResponse<T>

    if (responseJson.status !== PayloadStatus.OK) {
      throw new Error(
        `API request failed with status ${responseJson.status} - ${responseJson.message}`,
      )
    }
    return responseJson.data
  } catch (error) {
    // During Meticulous replay the network is stubbed, so any request that
    // doesn't match a recording returns an empty body and throws here. These are
    // replay artifacts, not real errors — don't emit a console error (Meticulous
    // tracks console errors separately from the meticulous-ignore'd DOM). We
    // still rethrow so callers handle the empty result exactly as in production.
    const underMeticulousReplay = typeof window !== 'undefined' && 'Meticulous' in window

    if (!underMeticulousReplay) {
      const status = error instanceof HttpError ? ` status=${error.status}` : ''
      const cacheKey = params.cacheKey ? ` cacheKey=${params.cacheKey}` : ''

      if (error instanceof Error) {
        logger.error(
          `Payload query error: collection=${params.collection}${status}${cacheKey}`,
          error,
        )
      } else {
        logger.error(
          `Payload query error: collection=${params.collection}${status}${cacheKey}`,
          String(error),
        )
      }
    }

    throw error
  }
}
